import type { Message } from '../components/practice/types';

/** Append `msg` if no row with the same `id` exists (stream + SSE). */
export function appendMessageDeduped(prev: Message[], msg: Message): Message[] {
  if (prev.some((m) => m.id === msg.id)) return prev;
  return [...prev, msg];
}

export type MergeStreamMessageOptions = {
  pendingOptimisticUserId: string | null;
  currentUserId: string | undefined;
};

/**
 * Apply a `message_added` event from the session stream: drop the local
 * optimistic user row when the persisted user row for the current user arrives
 * (by pending id, or by matching `opt-*` placeholder + content if the ref was
 * cleared early), then dedupe by id.
 */
export function mergeStreamMessage(
  prev: Message[],
  msg: Message,
  options: MergeStreamMessageOptions,
): { next: Message[]; clearedPending: boolean } {
  const { pendingOptimisticUserId, currentUserId } = options;
  let base = prev;
  let clearedPending = false;
  if (msg.role === 'user' && currentUserId && msg.userId === currentUserId) {
    if (pendingOptimisticUserId) {
      base = prev.filter((m) => m.id !== pendingOptimisticUserId);
      clearedPending = true;
    } else {
      // Ref may already be cleared (e.g. HTTP `finally`) while the optimistic row is still
      // on screen; drop any matching placeholder so we never briefly show two user bubbles.
      base = prev.filter(
        (m) =>
          !(
            m.id.startsWith('opt-') &&
            m.role === 'user' &&
            m.userId === currentUserId &&
            m.content === msg.content
          ),
      );
    }
  }
  const next = appendMessageDeduped(base, msg);
  return { next, clearedPending };
}
